import { SortConfig } from "../pages/UnifiedReminderPage/types/sortTypes";

export enum ReminderView {
  PATIENTS = "patients",
  ARTICLES = "articles",
  AGGREGATED = "aggregated",
}

export interface ViewOption {
  value: ReminderView;
  label: string;
  description: string;
  icon?: React.ReactNode;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface ReminderFilters {
  dateRange: DateRange;
  groups?: string;
  statuses?: string;
  search?: string;
  therapyTaken?: "all" | "taken" | "pending";
  sort?: SortConfig[]; // Optional to maintain backward compatibility
}

// Filter button props interface
export interface FilterButtonProps {
  label: string;
  value: string;
  hasFilter: boolean;
  isActive?: boolean;
  isVisible: boolean;
  onClick: () => void;
  className?: string;
}

// Modal type for filter modals
export type ModalType = "date" | "groups" | "status" | null;

export interface PatientReminderData {
  // Primary fields
  id: number;
  status: string;
  note: string;
  therapyTaken: boolean;
  willBeReadyFrom: string;
  willBeReadyTo: string;
  created: string; // Added - LocalDateTime from backend
  modified: string | null; // Added - LocalDateTime from backend

  // User fields (raw from API)
  userFirstName: string;
  userSecondName: string;
  userLBO: string;
  userPhone: string; // Renamed from phone for clarity
  userEmail?: string;
  userResponse?: {
    username?: string;
  };

  // Computed fields (for display compatibility)
  name: string; // userFirstName + userSecondName
  phone: string; // userPhone
  lbo: string; // userLBO
  email: string; // userEmail || userResponse?.username

  // Related data
  items: ReminderItem[];
  groups: Array<{ id: number; name: string }>;
  rfzoTherapyReminderGroupsIds?: any[];

  // Computed fields for pricing (added for compatibility)
  totalRFZOPrice?: number;
  rfzoPriceInfo?: {
    total: number;
    nullCount: number;
    itemCount: number;
    availableCount: number;
    hasNullValues: boolean;
  };

  // Index signature for additional dynamic fields
  [key: string]: any;
}

// Supporting interfaces for complete type safety
export interface FileResponse {
  id: number | null;
  location: string;
  thumbnail: string;
  type: string;
  priority: number;
  isSmall: number;
  imageLink: string;
}

export interface LicenceHolderResponse {
  // Define based on actual backend structure when available
  id?: number;
  name?: string;
}

export interface RelatedGroupShortResponse {
  // Define based on actual backend structure when available
  id?: number;
  name?: string;
}

export interface TherapyIndicationResponse {
  // Define based on actual backend structure when available
  id?: number;
  name?: string;
}

export interface CompleteItemResponse {
  id: number;
  name: string;
  tax: number;
  formula: number;
  genericName: string;
  jkl: string;
  atc: string;
  shape: string;
  intensity: string;
  packageShape: string;
  packageQuantity: string;
  itemLoyaltyPoints: number;
  itemLoyaltyPointsMedic: number;
  averageRate: number | null;
  numOfRates: number;
  rfzoPrice: number | null;
  rfzoParticipationPrice: number;
  manufacturerId: number;
  manufacturerName: string;
  manufacturerState: string;
  files: FileResponse[];
  itemAvailability: string;
  itemAvailabilityCode: string;
  itemAvailabilityShortCode: string;
  itemAvailabilityDescription: string;
  itemAvailabilityColor: string;
  itemAvailabilityNeedRP: number;
  itemEANs: string[];
  licenceHolderResponse: LicenceHolderResponse | null;
  commonGroups: RelatedGroupShortResponse[];
  therapyIndications: TherapyIndicationResponse[];
  images: FileResponse[]; // Deprecated but used for compatibility
}

export interface ReminderItem {
  id?: number;
  itemId?: number;
  rfzoQuantity: number;
  rfzoParticipationPrice: number; // Added - Critical field from backend
  rfzoPrice?: number | null;
  articleName?: string;
  itemResponse?: CompleteItemResponse; // Updated to use complete interface
}

export interface ArticleViewData {
  id: string; // Composite key: patientId-itemId
  articleName: string;
  articleId: number;
  patientName: string;
  patientLBO: string;
  patientId: number;
  quantity: number;
  dueDate: string;
  willBeReadyFrom: string;
  willBeReadyTo: string;
  reminderStatus: string;
  therapyTaken: boolean;
  note?: string;
  patientPhone?: string;
  patientEmail?: string;
  rfzoPrice?: number | null;
}

export interface AggregatedArticleData {
  articleName: string;
  articleId: number;
  totalQuantity: number;
  availableQuantity: number;
  reminderCount: number;
}

export interface UnifiedReminderState {
  selectedView: ReminderView;
  // ✅ FIX: Filters removed from state - read directly from useUnifiedFilters
  data: {
    patients: PatientReminderData[];
    articles: ArticleViewData[];
    aggregated: AggregatedArticleData[];
  };
  loading: {
    patients: boolean;
    articles: boolean;
    aggregated: boolean;
  };
  error: {
    patients: string | null;
    articles: string | null;
    aggregated: string | null;
  };
}
